import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  getAuditLogs,
  getBarangays,
} from '../../lib/api';
import {
  FileText,
  Search,
  User,
  Clock,
} from 'lucide-react';

export default function AuditLogs() {
  const { user, isMunicipalAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [logsRes, barangaysRes] = await Promise.all([
        getAuditLogs({ limit: 500 }),
        getBarangays(),
      ]);
      setLogs(logsRes.data);
      setBarangays(barangaysRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesEntity = filterEntity === 'all' || log.entity_type === filterEntity;
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    const matchesSearch = log.actor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesEntity && matchesAction && matchesSearch;
  });

  const getBarangayName = (id) => barangays.find(b => b.id === id)?.name || '';

  const getActionColor = (action) => {
    if (action.includes('CREATE') || action.includes('GENERATE')) return 'bg-green-100 text-green-800 border-green-300';
    if (action.includes('UPDATE') || action.includes('ASSIGN')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (action.includes('DELETE') || action.includes('DISABLE')) return 'bg-red-100 text-red-800 border-red-300';
    if (action.includes('EMERGENCY') || action.includes('ALERT')) return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-slate-100 text-slate-800 border-slate-300';
  };

  const entityTypes = [...new Set(logs.map(l => l.entity_type))];
  const actionTypes = [...new Set(logs.map(l => l.action))];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-logs">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
            Audit Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            Track all system activities and changes
          </p>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by actor or action..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-logs-input"
                />
              </div>
              <Select value={filterEntity} onValueChange={setFilterEntity}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Entity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {entityTypes.map((type) => (
                    <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Action type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {actionTypes.map((action) => (
                    <SelectItem key={action} value={action}>{action}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Activity Log
            </CardTitle>
            <CardDescription>
              {filteredLogs.length} entries
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <Table className="table-zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Barangay</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log) => (
                    <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(log.created_at).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{log.actor_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium border ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize text-xs">
                          {log.entity_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {getBarangayName(log.barangay_id) || '-'}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        {log.metadata && Object.keys(log.metadata).length > 0 ? (
                          <span className="text-xs text-muted-foreground">
                            {JSON.stringify(log.metadata).slice(0, 50)}...
                          </span>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredLogs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No logs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
